import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";
import { usePrefersReducedMotion } from "@/hooks/useReducedMotion";

/**
 * Hero — cinematic "door entry" reveal.
 *
 * The entire choreography is a SINGLE coordinated timeline driven by a
 * parent variants tree (initial="hidden" / animate={phase}). Every child
 * consumes the parent's variant state and applies its own precise delay,
 * so the timing is orchestrated in one place — not as independent
 * animations.
 *
 * TIMELINE (from t=0)
 *   0.00s   doors closed · wordmark visible on seam · hero content opacity 0
 *   0.40s   wordmark fades out (0.3s)
 *   0.70s   doors slide apart (1.10s, ease [0.76, 0, 0.24, 1])
 *   0.90s   hero photo settles 1.08 → 1.0 (1.4s, ease-out)
 *   1.40s   headline lines mask up (0.7s each, ease [0.22, 1, 0.36, 1], 90ms stagger)
 *   1.70s   eyebrow / sub / CTAs / detail card / scroll dial fade up (120ms stagger)
 *   2.40s   door panels UNMOUNT · Lenis scrolling resumes
 *
 * REDUCED MOTION: doors skipped, Lenis never stopped, all copy fades in
 * over 0.5s.
 *
 * TESTING: sessionStorage skip is DISABLED so the animation plays on every
 * reload. To re-enable, restore the SESSION_KEY guard in the mount effect.
 */

const HERO_IMG = "/hero/img5.png";

/* ============================================================
 * Hero slideshow — cinematic property rotation
 *
 * Six generated 16:9 property photos share one editorial 35mm-film
 * aesthetic. Each slide holds 6s then crossfades to the next over 1.5s
 * (opacity only — no slide/wipe). While a slide is active it also
 * ken-burns: 1.0 → 1.06 or 1.06 → 1.0, alternating per slide so the
 * motion direction never repeats.
 *
 * All 6 images are preloaded on mount so no transition ever flashes
 * empty. When the tab is hidden the rotation pauses (visibilitychange).
 * `prefers-reduced-motion` collapses the whole thing to a static
 * hero-01 with no ken-burns, no rotation, no crossfade.
 *
 * Object-position per slide is exposed as `position` so any image that
 * crops awkwardly at 9:16 can be nudged without touching layout.
 * ============================================================ */
const HERO_BASE =
  "https://dcvqrrbrkrkzgjwxjgph.supabase.co/storage/v1/object/public/properties/hero";
const HERO_SLIDES = [
  { url: `${HERO_BASE}/hero-01.jpg`, zoom: "in", position: "center" },
  { url: `${HERO_BASE}/hero-02.jpg`, zoom: "out", position: "center" },
  { url: `${HERO_BASE}/hero-03.jpg`, zoom: "in", position: "center" },
  { url: `${HERO_BASE}/hero-04.jpg`, zoom: "out", position: "center" },
  { url: `${HERO_BASE}/hero-05.jpg`, zoom: "in", position: "center" },
  { url: `${HERO_BASE}/hero-06.jpg`, zoom: "out", position: "center" },
];
const HERO_HOLD_MS = 6000;
const HERO_FADE_MS = 1500;
const HERO_TOTAL_MS = HERO_HOLD_MS + HERO_FADE_MS;

// Easings referenced by the spec.
const EASE_DOORS = [0.76, 0, 0.24, 1];
const EASE_LINE = [0.22, 1, 0.36, 1];
const EASE_STANDARD = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const reduced = usePrefersReducedMotion();
  const lenis = useLenis();

  // The single source of truth for the timeline. Flipping it from
  // "hidden" -> "show" triggers every child variant simultaneously; each
  // child's own `transition.delay` places it precisely in the sequence.
  const [phase, setPhase] = useState("hidden");
  const [doorsMounted, setDoorsMounted] = useState(!reduced);

  // Kick the sequence on next paint so the initial "hidden" state renders
  // to the DOM first — otherwise the browser can skip the transition.
  useEffect(() => {
    if (reduced) {
      // reduced-motion path: no doors, immediate fade-in
      setDoorsMounted(false);
      setPhase("show");
      return;
    }
    const id = requestAnimationFrame(() => setPhase("show"));
    return () => cancelAnimationFrame(id);
  }, [reduced]);

  // Lock Lenis scrolling during the reveal so mid-animation scroll can't
  // strand the doors half-open.
  useEffect(() => {
    if (reduced || !lenis) return;
    lenis.stop();
    const t = setTimeout(() => lenis.start(), 2400);
    return () => {
      clearTimeout(t);
      lenis.start();
    };
  }, [lenis, reduced]);

  // Unmount the door panels at t=2.4s so they can never intercept clicks.
  useEffect(() => {
    if (reduced) return;
    const t = setTimeout(() => setDoorsMounted(false), 2400);
    return () => clearTimeout(t);
  }, [reduced]);

  /* Scroll-out translate + fade — UNCHANGED from the previous hero.
   * Kept OUTSIDE the entry-variant tree so it doesn't interfere. */
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, -360]);
  const opacity = useTransform(scrollY, [0, 500, 800], [1, 0.5, 0]);

  /* -------- VARIANTS (all children consume the same `phase`) -------- */
  const parentV = { hidden: {}, show: {} };

  // Photo settles at t=0.9
  const photoV = reduced
    ? {
        hidden: { scale: 1, opacity: 0 },
        show: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
      }
    : {
        hidden: { scale: 1.08 },
        show: {
          scale: 1,
          transition: { delay: 0.9, duration: 1.4, ease: "easeOut" },
        },
      };

  // Doors wordmark fades OUT at t=0.4
  const wordmarkV = {
    hidden: { opacity: 1 },
    show: {
      opacity: 0,
      transition: { delay: 0.4, duration: 0.3, ease: "easeOut" },
    },
  };

  // Doors slide apart at t=0.7
  const leftDoorV = {
    hidden: { x: "0%" },
    show: {
      x: "-100%",
      transition: { delay: 0.7, duration: 1.1, ease: EASE_DOORS },
    },
  };
  const rightDoorV = {
    hidden: { x: "0%" },
    show: {
      x: "100%",
      transition: { delay: 0.7, duration: 1.1, ease: EASE_DOORS },
    },
  };

  // Headline lines mask up at t=1.4 (90ms stagger per line via custom i)
  const lineV = reduced
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.5 } },
      }
    : {
        hidden: { y: "110%" },
        show: (i) => ({
          y: "0%",
          transition: {
            delay: 1.4 + i * 0.09,
            duration: 0.7,
            ease: EASE_LINE,
          },
        }),
      };

  // Eyebrow / sub / CTAs / card / dial fade up at t=1.7 (120ms stagger)
  const fadeV = reduced
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.5 } },
      }
    : {
        hidden: { opacity: 0, y: 16 },
        show: (i) => ({
          opacity: 1,
          y: 0,
          transition: {
            delay: 1.7 + i * 0.12,
            duration: 0.6,
            ease: EASE_STANDARD,
          },
        }),
      };

  return (
    <motion.section
      className="relative overflow-hidden"
      style={{ height: "100svh", minHeight: 620, backgroundColor: "#14231B" }}
      data-nav-theme="dark"
      data-testid="hero"
      variants={parentV}
      initial="hidden"
      animate={phase}
    >
      {/* ============ full-bleed slideshow ============ */}
      <motion.div className="absolute inset-0" variants={photoV}>
        <HeroSlideshow reduced={reduced} />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(36,56,46,0.25) 0%, rgba(36,56,46,0.55) 55%, rgba(36,56,46,0.82) 100%)",
          }}
        />
      </motion.div>

      {/* ============ CONTENT (scroll-out wrapper unchanged) ============ */}
      <motion.div
        className="relative z-10 flex h-full flex-col justify-end"
        style={{ y, opacity }}
      >
        <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-10 px-6 pb-14 md:grid-cols-12 md:gap-8 md:px-10 md:pb-24">
          {/* -------- bottom-left copy -------- */}
          <div className="md:col-span-8">
            <HeroCopy fadeV={fadeV} lineV={lineV} />
          </div>

          {/* -------- bottom-right floating detail card -------- */}
          <div className="hidden md:col-span-4 md:flex md:justify-end">
            <FloatingStandardCard fadeV={fadeV} reduced={reduced} />
          </div>
        </div>

        {/* -------- bottom-center scroll dial -------- */}
        <ScrollDial fadeV={fadeV} reduced={reduced} />
      </motion.div>

      {/* ============ DOOR PANELS (overlay, one-time) ============
       * Rendered inside the same variants tree so the "hidden"/"show"
       * state flows into leftDoorV / rightDoorV / wordmarkV. Removed
       * from the DOM at t=2.4s via `doorsMounted`. */}
      <AnimatePresence>
        {doorsMounted && (
          <motion.div
            className="pointer-events-none absolute inset-0 z-40"
            initial={false}
            exit={{ opacity: 0, transition: { duration: 0.001 } }}
            data-testid="hero-doors"
          >
            {/* wordmark on the seam */}
            <motion.div
              className="absolute inset-0 z-10 flex items-center justify-center"
              variants={wordmarkV}
            >
              <span
                className="font-display"
                style={{
                  fontSize: 40,
                  color: "#EDE9E0",
                  letterSpacing: "0.02em",
                  lineHeight: 1,
                }}
                data-testid="hero-doors-wordmark"
              >
                RO<span style={{ color: "#E3B268" }}>A</span>M
              </span>
            </motion.div>

            {/* left door */}
            <motion.div
              className="absolute inset-y-0 left-0 w-1/2"
              style={{ backgroundColor: "#14231B" }}
              variants={leftDoorV}
              data-testid="hero-door-left"
            />
            {/* right door */}
            <motion.div
              className="absolute inset-y-0 right-0 w-1/2"
              style={{ backgroundColor: "#14231B" }}
              variants={rightDoorV}
              data-testid="hero-door-right"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

/* ============================================================
 * HERO COPY — eyebrow, mask-up headline, sub, buttons.
 * Consumes the parent's variant state; each element passes a
 * `custom` index so its delay/stagger comes from fadeV/lineV.
 * ============================================================ */
const HeroCopy = ({ fadeV, lineV }) => {
  return (
    <div className="max-w-[880px]" data-testid="hero-copy">
      {/* eyebrow — first in the t=1.7 fade stagger */}
      <motion.p
        variants={fadeV}
        custom={0}
        className="eyebrow"
        style={{ color: "#EDE9E0", letterSpacing: "0.24em" }}
        data-testid="hero-eyebrow"
      >
        Boutique rental management · Alberta
      </motion.p>

      <h1
        className="font-display mt-6"
        style={{
          fontSize: "clamp(56px, 8.5vw, 92px)",
          color: "#F8F5EF",
          lineHeight: 0.95,
          letterSpacing: "-0.015em",
        }}
        data-testid="hero-headline"
      >
        <span className="block overflow-hidden">
          <motion.span
            variants={lineV}
            custom={0}
            className="block"
            data-testid="hero-headline-line-1"
          >
            Your basecamp
          </motion.span>
        </span>
        <span className="block overflow-hidden">
          <motion.span
            variants={lineV}
            custom={1}
            className="block"
            data-testid="hero-headline-line-2"
          >
            for{" "}
            <span className="italic" style={{ color: "#E3B268" }}>
              living.
            </span>
          </motion.span>
        </span>
      </h1>

      <motion.p
        variants={fadeV}
        custom={1}
        className="mt-6 max-w-[54ch] text-[16px] leading-[1.55] md:text-[18px]"
        style={{ color: "rgba(237,233,224,0.86)" }}
        data-testid="hero-subcopy"
      >
        Exceptional rental properties, managed like they&rsquo;re our own. Go
        climb something, chase a waterfall, stay out late. We&rsquo;ll keep
        your property running smoothly.
      </motion.p>

      <motion.div
        variants={fadeV}
        custom={2}
        className="mt-8 flex flex-wrap items-center gap-4"
      >
        <Link
          to="/owners"
          className="pill pill-gold text-base"
          data-testid="hero-primary-cta"
        >
          I own a property
        </Link>
        <Link
          to="/residences"
          className="pill text-base"
          style={{
            border: "1px solid rgba(237,233,224,0.55)",
            color: "#F8F5EF",
            backgroundColor: "transparent",
          }}
          data-testid="hero-secondary-cta"
        >
          Find your home
        </Link>
      </motion.div>
    </div>
  );
};

/* ============================================================
 * FLOATING "ROAM STANDARD" CARD — paper chip bottom-right.
 * Consumes the fadeV variant with custom index 3.
 * ============================================================ */
const FloatingStandardCard = ({ fadeV, reduced }) => (
  <motion.aside
    className="w-[300px] max-w-full self-end"
    variants={fadeV}
    custom={3}
    style={{
      backgroundColor: "rgba(248,245,239,0.92)",
      borderRadius: 20,
      padding: "20px 22px",
      boxShadow: "0 30px 60px -30px rgba(20,35,27,0.5)",
      backdropFilter: "blur(10px)",
    }}
    data-testid="hero-standard-card"
  >
    <p className="label-caps text-[10px]" style={{ color: "#C89A4E" }}>
      The ROAM Standard
    </p>
    <p className="mt-3 text-[14px] leading-[1.5]" style={{ color: "#201E1A" }}>
      Photographer in 48 hours. Listed everywhere in four days. A report
      every Monday.
    </p>
    <div
      aria-hidden
      className="mt-4 h-[1px] w-16"
      style={{ backgroundColor: "#C89A4E" }}
    />
  </motion.aside>
);

/* ============================================================
 * SCROLL DIAL — 72px pine ring with curved caption + arrow.
 * Fade-in via fadeV (custom index 4); rotation is a separate loop
 * that runs continuously once the SVG is mounted.
 * ============================================================ */
const ScrollDial = ({ fadeV, reduced }) => {
  const CIRCUM_TEXT = "SCROLL DOWN · SCROLL DOWN · ";
  return (
    <motion.div
      className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2"
      variants={fadeV}
      custom={4}
      style={{ color: "#EDE9E0" }}
      data-testid="hero-scroll-dial"
    >
      <div className="relative" style={{ width: 72, height: 72 }}>
        <motion.svg
          viewBox="0 0 72 72"
          width="72"
          height="72"
          animate={reduced ? {} : { rotate: 360 }}
          transition={
            reduced ? {} : { duration: 20, repeat: Infinity, ease: "linear" }
          }
          style={{ transformOrigin: "50% 50%" }}
        >
          <defs>
            <path
              id="dial-circle"
              d="M 36,36 m -28,0 a 28,28 0 1,1 56,0 a 28,28 0 1,1 -56,0"
            />
          </defs>
          <circle
            cx="36"
            cy="36"
            r="34"
            fill="none"
            stroke="rgba(237,233,224,0.55)"
            strokeWidth="1"
          />
          <text
            fill="currentColor"
            style={{
              fontFamily: "Archivo, sans-serif",
              fontSize: 8,
              letterSpacing: "1.6px",
            }}
          >
            <textPath href="#dial-circle" startOffset="0">
              {CIRCUM_TEXT + CIRCUM_TEXT}
            </textPath>
          </text>
        </motion.svg>
        <span className="absolute inset-0 grid place-items-center">
          <ArrowDown size={16} strokeWidth={1.6} />
        </span>
      </div>
    </motion.div>
  );
};

/* ============================================================
 * HERO SLIDESHOW — ambient background rotation.
 * All slides stay mounted; only the active slide has opacity 1.
 * Transitions are opacity-only, ken-burns per-slide, tab-hidden
 * aware, reduced-motion aware.
 * ============================================================ */
const HeroSlideshow = ({ reduced }) => {
  const [index, setIndex] = useState(0);

  // Preload every slide so a crossfade never reveals a blank frame.
  useEffect(() => {
    HERO_SLIDES.forEach((s) => {
      const im = new window.Image();
      im.src = s.url;
    });
  }, []);

  // Rotate every HERO_TOTAL_MS. Skip entirely when reduced-motion or
  // the tab is hidden (pauses via visibilitychange listener).
  useEffect(() => {
    if (reduced) return;
    let hidden = document.hidden;
    const onVis = () => {
      hidden = document.hidden;
    };
    document.addEventListener("visibilitychange", onVis);
    const id = window.setInterval(() => {
      if (!hidden) setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, HERO_TOTAL_MS);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reduced]);

  return (
    <>
      <style>{`
        @keyframes hero-kb-in  { from { transform: scale(1.0); }  to { transform: scale(1.06); } }
        @keyframes hero-kb-out { from { transform: scale(1.06); } to { transform: scale(1.0); } }
      `}</style>
      {HERO_SLIDES.map((slide, i) => (
        <HeroSlide
          key={i}
          slide={slide}
          isActive={reduced ? i === 0 : i === index}
          reduced={reduced}
          slideIndex={i}
        />
      ))}
    </>
  );
};

const HeroSlide = ({ slide, isActive, reduced, slideIndex }) => {
  const imgRef = useRef(null);

  /* Restart the ken-burns animation every time this slide becomes
   * active. The .style.animation=none + reflow trick is the standard
   * way to re-trigger a CSS keyframe animation without remounting the
   * <img> (which would invalidate the browser's cached decode). */
  useEffect(() => {
    if (reduced || !isActive || !imgRef.current) return;
    const el = imgRef.current;
    el.style.animation = "none";
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    el.style.animation = `${
      slide.zoom === "in" ? "hero-kb-in" : "hero-kb-out"
    } ${HERO_TOTAL_MS}ms linear forwards`;
  }, [isActive, reduced, slide.zoom]);

  return (
    <div
      className="absolute inset-0"
      style={{
        opacity: isActive ? 1 : 0,
        transition: reduced ? "none" : `opacity ${HERO_FADE_MS}ms ease-in-out`,
        willChange: "opacity",
      }}
      data-testid={`hero-slide-${slideIndex}`}
      data-active={isActive ? "true" : "false"}
    >
      <img
        ref={imgRef}
        src={slide.url}
        alt=""
        draggable={false}
        className="h-full w-full object-cover"
        style={{
          objectPosition: slide.position || "center",
          willChange: reduced ? "auto" : "transform",
        }}
      />
    </div>
  );
};

export default Hero;
